import { ChangeDetectionStrategy, Component, input, output } from '@angular/core';

/**
 * Accessible on/off switch (`role="switch"` + `aria-checked`).
 *
 * Controlled component: the parent owns the state and reacts to
 * `checkedChange` (e.g. by PATCHing the API or updating a form control).
 * The button keeps a ≥44px hit area (WCAG touch target) around the
 * visually smaller track.
 */
@Component({
  selector: 'zag-switch',
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <button
      type="button"
      class="zag-switch"
      role="switch"
      [attr.aria-checked]="checked()"
      [attr.aria-label]="ariaLabel()"
      [disabled]="disabled()"
      (click)="onToggle()"
    >
      <span class="zag-switch__track" aria-hidden="true">
        <span class="zag-switch__thumb"></span>
      </span>
    </button>
  `,
  styles: `
    :host {
      display: inline-flex;
      align-items: center;
    }

    .zag-switch {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      /* 44px minimum touch target around the smaller visual track. */
      min-width: calc(var(--spacing-base) * 5.5);
      min-height: calc(var(--spacing-base) * 5.5);
      padding: var(--spacing-2xs);
      border: none;
      border-radius: var(--border-radius-full);
      background: transparent;
      cursor: pointer;

      &:focus-visible {
        outline: 2px solid var(--color-primary);
        outline-offset: 2px;
      }

      &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
      }
    }

    .zag-switch__track {
      display: flex;
      align-items: center;
      box-sizing: border-box;
      width: calc(var(--spacing-base) * 4.5);
      height: calc(var(--spacing-base) * 2.5);
      padding: var(--spacing-2xs);
      border: 1px solid var(--border-color-default);
      border-radius: var(--border-radius-full);
      background: var(--background-color-tertiary);
      transition: background var(--transition-duration-fast, 150ms);
    }

    .zag-switch[aria-checked='true'] .zag-switch__track {
      border-color: var(--color-success);
      background: var(--color-success);
    }

    .zag-switch__thumb {
      width: calc(var(--spacing-base) * 1.75);
      height: calc(var(--spacing-base) * 1.75);
      border-radius: var(--border-radius-full);
      background: var(--background-color-elevated);
      box-shadow: var(--shadow-sm);
      transition: transform var(--transition-duration-fast, 150ms);
    }

    .zag-switch[aria-checked='true'] .zag-switch__thumb {
      transform: translateX(var(--spacing-md));
    }

    @media (prefers-reduced-motion: reduce) {
      .zag-switch__track,
      .zag-switch__thumb {
        transition: none;
      }
    }
  `,
})
export class ZagSwitch {
  /** Current on/off state — the parent owns it (controlled component). */
  readonly checked = input.required<boolean>();
  /** Accessible name of the switch (there is no visible label). */
  readonly ariaLabel = input.required<string>();
  readonly disabled = input<boolean>(false);

  /** Emits the requested next state on every toggle. */
  readonly checkedChange = output<boolean>();

  protected onToggle(): void {
    this.checkedChange.emit(!this.checked());
  }
}
